import { supabase } from "./supabaseClient";

export const TOURNAMENT_PLAN_ORDER = {
  free: 0,
  basic: 1,
  starter: 2,
  standard: 2,
  pro: 3,
  club: 4,
};

const FREE_LIMITS = { active_tournaments: 1, participants: 16 };
const TIER_LIMITS = {
  free: FREE_LIMITS,
  basic: { active_tournaments: 1, participants: 16 },
  starter: { active_tournaments: 1, participants: 16 },
  standard: { active_tournaments: 1, participants: 16 },
  pro: { active_tournaments: 1, participants: 16 },
  club: { active_tournaments: null, participants: 16 },
};

const tierRank = (tier) => TOURNAMENT_PLAN_ORDER[tier || "free"] ?? 0;

export const TOURNAMENT_FORMATS = {
  single_elimination: { key: "single_elimination", label: "Single elimination", mode: "knockout", config: { bracket: "single_elimination", losses_to_eliminate: 1 } },
  double_elimination: { key: "double_elimination", label: "Double elimination", mode: "knockout", config: { bracket: "double_elimination", losses_to_eliminate: 2 } },
  round_robin: { key: "round_robin", label: "Round robin", mode: "point_based", config: { legs: 1, points: { win: 3, draw: 1, loss: 0 }, tiebreakers: ["points", "goal_difference", "goals_for"] } },
  double_round_robin: { key: "double_round_robin", label: "Double round robin", mode: "point_based", config: { legs: 2, points: { win: 3, draw: 1, loss: 0 }, tiebreakers: ["points", "goal_difference", "goals_for", "head_to_head"] } },
  group_to_knockout: { key: "group_to_knockout", label: "Groups into knockout", mode: "point_based", config: { stages: ["groups", "knockout"], group_size: 4, advance_per_group: 2 } },
  consolation_bracket: { key: "consolation_bracket", label: "Consolation / placement matches", mode: "knockout", config: { bracket: "consolation_bracket", placement_matches: true } },
};

export const TOURNAMENT_PACKAGE_OPTIONS = {
  tournaments_free: {
    key: "tournaments_free",
    name: "Free Tournament",
    tier: "free",
    priceLabel: "Free",
    maxParticipants: 16,
    platformFeeRate: 0,
    publicPage: "premium",
    formats: ["single_elimination"],
  },
  tournaments_basic_one_time: {
    key: "tournaments_basic_one_time",
    name: "Basic Tournament",
    tier: "basic",
    priceLabel: "Paid",
    maxParticipants: 16,
    platformFeeRate: 0,
    publicPage: "premium",
    formats: ["single_elimination"],
  },
  tournaments_standard_one_time: {
    key: "tournaments_standard_one_time",
    name: "Standard Tournament",
    tier: "standard",
    priceLabel: "Paid",
    maxParticipants: 16,
    platformFeeRate: 0,
    publicPage: "premium",
    formats: ["single_elimination", "round_robin", "group_to_knockout"],
  },
  tournaments_pro_one_time: {
    key: "tournaments_pro_one_time",
    name: "Pro Tournament",
    tier: "pro",
    priceLabel: "Paid",
    maxParticipants: 16,
    platformFeeRate: 0,
    publicPage: "premium",
    formats: ["single_elimination", "double_elimination", "round_robin", "double_round_robin", "group_to_knockout", "consolation_bracket"],
  },
};

const PACKAGE_ENTITLEMENT_KEYS = {
  "tournaments.package.basic_event": "tournaments_basic_one_time",
  "tournaments.package.standard_event": "tournaments_standard_one_time",
  "tournaments.package.pro_event": "tournaments_pro_one_time",
};

const isCurrentlyActive = (row) => {
  if (!["active", "trialing"].includes(row?.status)) return false;
  if (!row?.expires_at) return true;
  return new Date(row.expires_at).getTime() > Date.now();
};

export const hasTournamentTier = (access, tier) =>
  tierRank(access?.tier) >= TOURNAMENT_PLAN_ORDER[tier];

export const hasTournamentEntitlement = (access, key) =>
  access?.entitlements?.has(key);

export const getDefaultTournamentAccess = () => ({
  tier: "free",
  planName: "Tournaments Free",
  source: "free",
  status: "active",
  expiresAt: null,
  renewsAt: null,
  platformFeeRate: null,
  limits: FREE_LIMITS,
  entitlements: new Set([
    "tournaments.create.basic",
    "tournaments.dashboard.basic",
    "tournaments.format.single_elimination",
  ]),
  products: [],
  prices: [],
  activeEntitlements: [],
  activeSubscription: null,
  availablePackages: [{ ...TOURNAMENT_PACKAGE_OPTIONS.tournaments_free, source: "free" }],
});

export const loadTournamentAccess = async (profileId) => {
  const base = getDefaultTournamentAccess();
  if (!profileId) return base;

  const [
    { data: products },
    { data: prices },
    { data: entitlements },
    { data: subscriptions },
  ] = await Promise.all([
    supabase.from("billing_products").select("*").eq("product_line", "tournaments").eq("active", true),
    supabase
      .from("billing_prices")
      .select("*, billing_products!inner(product_line, product_key, name, metadata)")
      .eq("active", true)
      .eq("billing_products.product_line", "tournaments"),
    supabase
      .from("customer_entitlements")
      .select("*, entitlement_definitions(entitlement_key,name,product_line), billing_products(product_key,name,metadata), billing_prices(price_key,amount_cents,coin_amount,interval)")
      .eq("profile_id", profileId),
    supabase
      .from("billing_subscriptions")
      .select("*, billing_products(product_key,name,metadata), billing_prices(price_key,amount_cents,currency,interval)")
      .eq("profile_id", profileId)
      .in("status", ["trialing", "active", "past_due"])
      .order("created_at", { ascending: false }),
  ]);

  const activeEntitlements = (entitlements || []).filter((row) => {
    const key = row?.entitlement_definitions?.entitlement_key || "";
    return key.startsWith("tournaments.") && isCurrentlyActive(row);
  });

  const availablePackages = [{ ...TOURNAMENT_PACKAGE_OPTIONS.tournaments_free, source: "free" }];
  activeEntitlements.forEach((row) => {
    const entitlementKey = row?.entitlement_definitions?.entitlement_key;
    const packageKey = PACKAGE_ENTITLEMENT_KEYS[entitlementKey];
    if (!packageKey) return;
    if (row?.target_type && row.target_type !== "profile") return;
    const usageLimit = typeof row?.usage_limit === "number" ? row.usage_limit : 1;
    const usageCount = typeof row?.usage_count === "number" ? row.usage_count : 0;
    if (usageCount >= usageLimit) return;

    const product = row?.billing_products;
    const metadata = product?.metadata || {};
    const basePackage = TOURNAMENT_PACKAGE_OPTIONS[packageKey];
    if (!basePackage) return;
    availablePackages.push({
      ...basePackage,
      maxParticipants: Math.min(metadata?.limits?.participants || basePackage.maxParticipants, 16),
      platformFeeRate: basePackage.platformFeeRate,
      formats: Array.isArray(metadata?.formats) && metadata.formats.length ? metadata.formats : basePackage.formats,
      source: "entitlement",
      entitlementRow: row,
      product,
    });
  });

  const entitlementKeys = new Set(base.entitlements);
  activeEntitlements.forEach((row) => {
    const key = row?.entitlement_definitions?.entitlement_key;
    if (key) entitlementKeys.add(key);
  });

  const bestEntitlement = activeEntitlements
    .map((row) => ({ row, tier: row?.billing_products?.metadata?.tier || "free" }))
    .sort((a, b) => tierRank(b.tier) - tierRank(a.tier))[0];

  const activeSubscription = (subscriptions || []).find((row) => {
    const tier = row?.billing_products?.metadata?.tier;
    return tier && tierRank(tier) > 0;
  }) || null;

  const selectedProduct =
    bestEntitlement?.row?.billing_products ||
    activeSubscription?.billing_products ||
    (products || []).find((p) => p.product_key === "tournaments_free");

  const tier = selectedProduct?.metadata?.tier || "free";
  const tierDefaults = TIER_LIMITS[tier] || FREE_LIMITS;
  const limits = selectedProduct?.metadata?.limits || tierDefaults;
  const platformFeeRate =
    selectedProduct?.metadata?.platform_fee_rate ??
    selectedProduct?.metadata?.registration_fee_rate ??
    null;

  return {
    tier,
    planName: selectedProduct?.name || base.planName,
    source: bestEntitlement?.row?.source || (tier === "free" ? "free" : activeSubscription?.provider || "manual"),
    status: bestEntitlement?.row?.status || activeSubscription?.status || "active",
    expiresAt: bestEntitlement?.row?.expires_at || null,
    renewsAt: activeSubscription?.current_period_end || null,
    platformFeeRate,
    limits: {
      active_tournaments: typeof limits.active_tournaments === "number" ? limits.active_tournaments : tierDefaults.active_tournaments,
      participants: Math.min(typeof limits.participants === "number" ? limits.participants : tierDefaults.participants || 16, 16),
    },
    entitlements: entitlementKeys,
    products: products || [],
    prices: prices || [],
    activeEntitlements,
    activeSubscription,
    availablePackages,
  };
};
